import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { navLink, switchLangPath } from "../../utils/i18nRoutes";

export default function Header() {
  const { t, i18n } = useTranslation("common");
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const currentLang = i18n.language;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = currentLang === "es" ? "en" : "es";
    i18n.changeLanguage(newLang);

    const nextPath = switchLangPath(location.pathname, newLang);
    window.REACT_APP_NAVIGATE(nextPath);
  };

  const getNavLink = (path: string) => navLink(currentLang as any, path);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white shadow-[0_2px_12px_rgba(11,35,64,0.06)]"
          : "bg-white"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <Link to={getNavLink("/")} className="flex items-center z-50">
          <picture>
            <source
              srcSet="/images/VELXIA_fondoBlanco.webp"
              type="image/webp"
            />
            <img
              src="/images/VELXIA_fondoBlanco.png"
              alt="VELXIA"
              className="h-10 sm:h-14 md:h-16 w-auto"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-9">
          <Link
            to={getNavLink("/")}
            className="text-[15px] text-[#123A66] hover:text-[#0B2340] transition-colors duration-200 relative group whitespace-nowrap"
          >
            {t("nav.home")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B2340] transition-all duration-200 group-hover:w-full"></span>
          </Link>
          <Link
            to={getNavLink("/servicios")}
            className="text-[15px] text-[#123A66] hover:text-[#0B2340] transition-colors duration-200 relative group whitespace-nowrap"
          >
            {t("nav.services")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B2340] transition-all duration-200 group-hover:w-full"></span>
          </Link>
          <Link
            to={getNavLink("/soluciones-a-medida")}
            className="text-[15px] text-[#123A66] hover:text-[#0B2340] transition-colors duration-200 relative group whitespace-nowrap"
          >
            {t("nav.customSolutions")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B2340] transition-all duration-200 group-hover:w-full"></span>
          </Link>
          <Link
            to={getNavLink("/contacto")}
            className="text-[15px] text-[#123A66] hover:text-[#0B2340] transition-colors duration-200 relative group whitespace-nowrap"
          >
            {t("nav.contact")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B2340] transition-all duration-200 group-hover:w-full"></span>
          </Link>
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-2 sm:px-3 py-1.5 border border-[#E6E9EE] rounded-lg text-xs sm:text-sm text-[#123A66] hover:border-[#123A66] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            aria-label={
              currentLang === "es" ? "Cambiar a inglés" : "Switch to Spanish"
            }
          >
            <span className="font-medium">{currentLang.toUpperCase()}</span>
            <i className="ri-arrow-down-s-line text-base"></i>
          </button>

          <a
            href="https://wa.me/34694220264?text=Hola%2C%20me%20gustar%C3%ADa%20obtener%20m%C3%A1s%20informaci%C3%B3n"
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-[#25D366] rounded-full shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer"
            aria-label="WhatsApp"
          >
            <i className="ri-whatsapp-line text-white text-lg sm:text-xl"></i>
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-9 h-9 flex items-center justify-center text-[#123A66] cursor-pointer"
            aria-label="Menu"
          >
            <i
              className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-[#E6E9EE] shadow-lg">
          <nav className="px-4 py-4 space-y-1">
            <Link
              to={getNavLink("/")}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-[15px] text-[#123A66] hover:bg-[#F8F9FB] rounded-lg transition-colors cursor-pointer"
            >
              {t("nav.home")}
            </Link>
            <Link
              to={getNavLink("/servicios")}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-[15px] text-[#123A66] hover:bg-[#F8F9FB] rounded-lg transition-colors cursor-pointer"
            >
              {t("nav.services")}
            </Link>
            <Link
              to={getNavLink("/soluciones-a-medida")}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-[15px] text-[#123A66] hover:bg-[#F8F9FB] rounded-lg transition-colors cursor-pointer"
            >
              {t("nav.customSolutions")}
            </Link>
            <Link
              to={getNavLink("/contacto")}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-[15px] text-[#123A66] hover:bg-[#F8F9FB] rounded-lg transition-colors cursor-pointer"
            >
              {t("nav.contact")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
